import React, {
  Fragment, useEffect, useMemo,
  useState,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { EnelXGuiComponents, EnelXGuiPropTypes, EnelXGuiUtils } from 'gui';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import { Vertical } from 'utils';

const { MigrationMainSidebar, MigrationSearchElement, MigrationSelected } = EnelXGuiComponents;
const { SearchbarOutputShape } = EnelXGuiPropTypes;
const { arrUpsert, normalizeSelectedArray } = EnelXGuiUtils;

const AllFilter = ({
  isSidebarOpen, close, elements, onSubmit, filters, defaultFilters,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [outputs, setOutputs] = useState([]);
  const searchbarElements = useMemo(() => (
    elements({ outputs }).map((element) => ({
      ...element,
      componentProps: {
        ...element.componentProps, width: '100%', selectWidth: '100%', inheritWidth: true,
      },
    }))
  ),
  [elements, outputs]);

  useEffect(() => {
    if (!isSidebarOpen) return;
    const filterCopy = [...filters];
    if (filters.length !== outputs.length || !filters.every(
      ({ id, value }) => outputs.some((output) => output.id === id && output.value === value),
    )) {
      onSubmit(filterCopy);
    }
    setOutputs(filterCopy);
  }, [filters, isSidebarOpen]);

  const handleElementChange = ({ value, id }) => setOutputs(arrUpsert(outputs, [{ value, id }]));

  const handleSubmit = () => {
    onSubmit(outputs);
    close();
  };

  const reset = () => setOutputs(defaultFilters);

  const render = () => (
    <Vertical gap={theme.searchbar.sizes.gap}>
      {
        searchbarElements.map((element, i) => (
          <Fragment key={`ALLFILTER-ELEMENT-${element.id}-${i}`}>
            <MigrationSearchElement
              element={element}
              onChange={handleElementChange}
              allFilter
            />
            {!isMobileOnly ? (
              // HERE I AM SPREADING THE CHIPS (EACH MULTISELECT ELEMENT FOR EXAMPLE HAS IT'S OWN CHIP) AND DISPLAYING THE POPULATED FIELDS
              normalizeSelectedArray(outputs
                .filter((el) => el.id === element.id || element.componentProps.arr?.some((opt) => opt.id === el.id)))
                .map((el, index) => (
                  <MigrationSelected
                    key={`ALLFILTER-SELECTED-${el.id}-${i}-${index}`}
                    outputs={outputs}
                    element={el}
                    elements={elements}
                    onRemove={setOutputs}
                    allFilter
                  />
                ))
            ) : null}
          </Fragment>
        ))
      }
    </Vertical>
  );

  return (
    <MigrationMainSidebar
      title={t`components.allFilters`}
      close={close}
      submit={handleSubmit}
      submitText={t`components.search`}
      extraBtn={{
        function: reset,
        text: t('components.reset'),
        isLink: true,
      }}
      render={render}
      isShown={isSidebarOpen}
      showFooter
      width="25rem"
    />
  );
};

AllFilter.defaultName = 'AllFilter';

AllFilter.defaultProps = {
  filters: [],
  defaultFilters: [],
};

AllFilter.propTypes = {
  isSidebarOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  elements: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  filters: SearchbarOutputShape,
  defaultFilters: SearchbarOutputShape,
};

export default AllFilter;
